// Hydrofon-kontrakt — PASSIV marin akustikk som sensor i SKADE (NJORD-post).
//
// HVORFOR DENNE FINNES: trusselen som betyr mest for en kystobservasjonspost er
// små USV-er og planende RIB-er. De er små på radar (lav RCS, ligger i
// sjøklotter) men HØYE i vannet — en planende skrog/propell-kombinasjon
// kaviterer kraftig og bærer langt. Hydrofonen er derfor ofte FØRSTEdeteksjonen,
// ikke et tillegg. Kontrakten er en søster av `spark.ts` (akustisk skuddeteksjon
// i luft), men de fysiske forholdene er så ulike at feltene ikke kan gjenbrukes:
// annen referansetrykk, annen lydhastighet, andre analyser.
//
// TRE TING SOM ER MODELLERT BEVISST STRENGT — les dem før du endrer noe:
//
//  1) dB UTEN REFERANSE ER MENINGSLØST. Marin akustikk bruker 1 µPa som
//     referansetrykk (ISO 18405), luftakustikk bruker 20 µPa — 26 dB forskjell.
//     Derfor bærer HVERT nivåfelt referansen i navnet
//     (`bredbandNivaDbRe1uPa`). Et felt som bare het `niva_db` ville før eller
//     senere blitt lest med feil referanse.
//     Nivåene her er MOTTATTE nivå ved hydrofonen, ikke kildenivå. Kildenivå
//     (ISO 18405: dB re 1 µPa²m²) krever en transmisjonstapsmodell OG avstand.
//     Vi har ingen av dem passivt, så vi regner det ikke ut og later ikke som.
//
//  2) EN ENKELT HYDROFON GIR INGEN PEILING. En trykkfølsom hydrofon er
//     omnidireksjonell — den sier «noe er der», ingenting om hvor. Derfor er
//     `HydrofonDeteksjon` en DISKRIMINERT UNION: `modus: 'enkelt'` har ikke noe
//     peilingsfelt i det hele tatt, mens `modus: 'array'` har PÅKREVD peiling
//     med usikkerhet. En valgfri `peilingDeg?` ville blitt lest som 0 (nord) av
//     første klient som glemte å sjekke `undefined`. Det er en løgn om
//     situasjonsbildet, og det er slik man skyter mot nord.
//
//  3) PASSIV AKUSTIKK MÅLER IKKE AVSTAND. Én post gir aldri en posisjon —
//     verken med eller uten array. Posisjon krever flere poster (krysspeiling
//     eller TDOA). `hydrofonDeteksjonTilTrack()` plasserer derfor sporet på
//     HYDROFONENS posisjon, merket `estimated`, med avstand eksplisitt «ukjent»
//     — ikke et projisert punkt på en gjettet avstand.
//
// UVERIFISERT-merking: alt som er en antakelse fra litteratur/erfaring og ikke
// målt i Oslofjorden er merket UVERIFISERT i kommentaren. Terskelverdiene i
// `grovKlasseFraDemon()` er det tydeligste eksempelet.
import { z } from 'zod';
import { project, haversineM, featureCollection } from './geo.js';
// ── Grenser (DoS-vern, jf. spark.ts MAX_BATCH) ──────────────────────────────
/** Maks tersband-oppføringer i én deteksjon (10 Hz–40 kHz i 1/3-oktav ≈ 37). */
export const MAX_TERSBAND = 64;
/** Maks tonale komponenter rapportert per deteksjon. */
export const MAX_TONALER = 64;
/** Maks poster i én lokaliseringsberegning. */
export const MAX_POSTER = 32;
// ── Kalibrering ─────────────────────────────────────────────────────────────
/**
 * Kalibreringsstatus. Dette er ikke administrativt pynt: uten kjent følsomhet
 * (V/µPa) og kjent forsterkning kan et opptak IKKE gjøres om til absolutt
 * lydtrykk. Da er «dB re 1 µPa» feil merkelapp på tallet, og eneste ærlige bruk
 * er relativ sammenligning mot postens eget støygulv.
 */
export const KalibreringStatus = z.enum([
    'kalibrert', // gyldig sertifikat → absolutte nivå kan oppgis
    'fabrikkverdi', // nominell følsomhet fra datablad → absolutt nivå ±3 dB (UVERIFISERT slark)
    'utløpt', // var kalibrert, sertifikatet er utløpt → behandles som relativ
    'ukalibrert', // ingen kjent følsomhet → NIVÅ ER RELATIVT
]);
export const Kalibrering = z.object({
    status: KalibreringStatus,
    /**
     * Hydrofonens følsomhet i dB re 1 V/µPa. Alltid NEGATIV (typisk −200 … −150):
     * 1 µPa gir langt under 1 V. Et positivt tall her betyr at noen har snudd
     * fortegnet, og da blir alle nivå feil med 2× følsomheten.
     */
    folsomhetDbReV1uPa: z.number().max(0).optional(),
    /** Forsterkning i kjeden (preamp + ADC) i dB. Trekkes fra ved nivåberegning. */
    forsterkningDb: z.number().optional(),
    sistKalibrertMs: z.number().int().nonnegative().optional(),
    gyldigTilMs: z.number().int().nonnegative().optional(),
    /** Hvem/hva som kalibrerte (sertifikatnummer, lab). Sporbarhet. */
    referanse: z.string().max(120).optional(),
});
// ── Array-geometri ──────────────────────────────────────────────────────────
/**
 * Array-form. Formen bestemmer hvilke TVETYDIGHETER peilingen har — den er
 * derfor ikke kosmetikk:
 *  - `lineær`: kan bare måle konusvinkelen til aksen → speilpeiling om aksen
 *    (babord/styrbord kan ikke skilles). Dette er normaltilfellet for et
 *    bunnlagt eller slept array.
 *  - `plan`: løser azimut, men har for/bak-tvetydighet uten baffel.
 *  - `volum`: 3D, i praksis entydig.
 *  - `vektor`: vektorsensor/DIFAR — måler partikkelhastighet i to/tre akser og
 *    gir entydig azimut fra ETT punkt. Fysisk et enkeltpunkt, men behandles som
 *    array her fordi det GIR peiling.
 */
export const ArrayForm = z.enum(['lineær', 'plan', 'volum', 'vektor']);
export const ArrayElement = z.object({
    indeks: z.number().int().min(0),
    /**
     * Elementets plass i ARRAY-RAMMEN, meter:
     *   x = langs `referanseretningDeg`, y = 90° til høyre for den, z = ned.
     * Lokale meter (ikke lat/lon) fordi elementavstanden er cm–m og
     * grader ville tapt oppløsning i flyttall.
     */
    xM: z.number(),
    yM: z.number(),
    zM: z.number().default(0),
});
export const ArrayGeometri = z.object({
    form: ArrayForm,
    /** Minst 2 elementer — ett element er per definisjon ikke et array. */
    elementer: z.array(ArrayElement).min(2).max(256),
    /**
     * Sann peiling (grader fra nord) som array-rammens +x-akse peker mot.
     * PÅKREVD. Uten den er alle peilinger RELATIVE og kan ikke tegnes på kart —
     * samme problem som radar uten heading (se radar.ts `spokePeiling`).
     */
    referanseretningDeg: z.number().min(0).max(360),
    /** 1σ usikkerhet i referanseretningen. Går rett inn i peilingsbudsjettet. */
    referanseretningUsikkerhetDeg: z.number().min(0).max(90).default(0),
    /** Er formen oppmålt etter utlegg, eller antatt fra datablad? */
    geometriKilde: z.enum(['oppmålt', 'datablad', 'antatt']).default('antatt'),
});
// ── Posten ──────────────────────────────────────────────────────────────────
export const HydrofonType = z.enum(['enkelt', 'array']);
/**
 * Tidssynkronisering. Avgjørende for TDOA: 1 ms klokkefeil ≈ 1,5 m posisjonsfeil
 * (c ≈ 1480 m/s). GPS/PPS gir µs. NTP over mobilnett gir 10-tallet ms → 10-tallet
 * meter, i praksis grensen for hva som er brukbart. Ingen synk → TDOA er ikke
 * bare unøyaktig, den er meningsløs, og `tdoaLokalisering()` nekter da å regne.
 */
export const Tidssync = z.enum(['gps-pps', 'ntp', 'ingen']);
export const HydrofonPostSkjema = z
    .object({
    postId: z.string().min(1).max(64),
    navn: z.string().min(1).max(80),
    /** Hydrofonens/array-referansepunktets posisjon. PÅKREVD — en akustisk
     *  deteksjon uten kjent sensorposisjon kan ikke brukes til noe geografisk. */
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** Hydrofonens dybde under havflaten, meter POSITIV NED. */
    dybdeM: z.number().min(0).max(11000),
    /** Havdybden på stedet, om kjent. Styrer flerveisutbredelse (bunn/overflate)
     *  og dermed hvor mye nivået svinger — grunt vann er en verre kanal. */
    havdybdeM: z.number().min(0).max(11000).optional(),
    type: HydrofonType,
    /** KUN og ALLTID for `type: 'array'` — håndhevet under. */
    array: ArrayGeometri.optional(),
    kalibrering: Kalibrering,
    tidssync: Tidssync.default('ingen'),
    /** 1σ klokkeusikkerhet i millisekunder, om posten kjenner sin egen. */
    klokkeusikkerhetMs: z.number().min(0).max(60000).optional(),
    /** Postens eget målte støygulv i analysebåndet — referansen «stille sjø» må
     *  måles per post, den kan ikke antas (skipstrafikk, strøm, regn). */
    stoyGulvDbRe1uPa: z.number().optional(),
    /** Org-tilhørighet håndteres av Track/RBAC; her holder vi bare eierskapet. */
    eier: z.string().max(64).optional(),
})
    .superRefine((p, ctx) => {
    // Type og geometri MÅ stemme. Et «array» uten elementgeometri kan ikke peile,
    // og en «enkelt» hydrofon med array-geometri er en selvmotsigelse som ville
    // fått nedstrøms kode til å tro at peiling er mulig.
    if (p.type === 'array' && !p.array) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['array'],
            message: 'type «array» krever elementgeometri + referanseretning',
        });
    }
    if (p.type === 'enkelt' && p.array) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['array'],
            message: 'type «enkelt» kan ikke ha array-geometri (en hydrofon peiler ikke)',
        });
    }
});
// ── Signatur ────────────────────────────────────────────────────────────────
/** Én tonal (smalbånds) komponent. LOFAR-analysen finner disse. */
export const TonalKomponent = z.object({
    frekvensHz: z.number().positive().max(200000),
    /** Nivå i tonens bånd, dB re 1 µPa. Referansen står i navnet med vilje. */
    nivaDbRe1uPa: z.number(),
    /** Båndbredden nivået er integrert over. En «tone» uten båndbredde kan ikke
     *  sammenlignes mot et bredbåndsnivå — det er W/Hz mot W. */
    bandbreddeHz: z.number().positive().optional(),
    /** Om linja er tolket som harmonisk nr. n av en grunntone (n=1 er grunntonen). */
    harmoniskNr: z.number().int().min(1).max(64).optional(),
});
/**
 * DEMON-estimat (Detection of Envelope MOdulation On Noise): kavitasjonsstøyen
 * båndpassfiltreres, amplitudedemoduleres, og modulasjonsspekteret viser linjer
 * på akselrate (omdreining/s) og bladerate (= akselrate × antall blader).
 * LOFAR er søskenanalysen på selve lydtrykket (maskinlinjer, gir, aggregat).
 *
 * MERK ENHET: ratene er i Hz (omdreininger per sekund), ikke rpm. Hz fordi det
 * er det spekteret faktisk gir; rpm = Hz × 60 gjøres i UI om operatøren vil det.
 */
export const DemonEstimat = z.object({
    /** Akselrate (shaft rate) i Hz — grunnlinja i DEMON-spekteret. */
    akselrateHz: z.number().positive().max(500).optional(),
    /** Bladerate (blade rate / blade passing frequency) i Hz. */
    bladerateHz: z.number().positive().max(2000).optional(),
    /**
     * Antall blader. UTLEDET som bladerate/akselrate — IKKE målt. Se
     * `estimerAntallBlader()` for hvorfor dette ofte er feil.
     */
    antallBladerEstimat: z.number().int().min(1).max(12).optional(),
    /** Antall aksler/propeller spekteret antyder (to sett linjer = to motorer). */
    antallAkslerEstimat: z.number().int().min(1).max(8).optional(),
    /** Kaviterer propellen? Kavitasjon er selve forutsetningen for DEMON. */
    kavitasjon: z.boolean().optional(),
    /** Båndet DEMON demodulerte i (kavitasjonsstøy ligger typisk 1–20 kHz). */
    demodBandFraHz: z.number().positive().optional(),
    demodBandTilHz: z.number().positive().optional(),
    /** 0–1 på RATE-estimatet (linjestyrke/stabilitet), ikke på klassen. */
    konfidens: z.number().min(0).max(1),
});
/**
 * Grov klasse. NB: settet er IKKE ortogonalt, og det er en erkjent svakhet i
 * kontrakten, ikke en feil i dataene: `planende-fartøy`/`sakte-fartøy` beskriver
 * FARTSREGIME, mens `vannjet`/`påhengsmotor` beskriver FREMDRIFT. En planende RIB
 * har normalt påhengsmotor — begge er sanne samtidig. Derfor finnes
 * `alternativeKlasser` i signaturen: vi rangerer, vi utelukker ikke.
 */
export const HydrofonKlasse = z.enum([
    'planende-fartøy', // høy fart, kraftig kavitasjon, bredbånd dominerer
    'sakte-fartøy', // deplasement/trålfart, lav akselrate, tydelige lave harmoniske
    'vannjet', // dyseanlegg: mange blader, høy rate, svake/ingen DEMON-linjer
    'påhengsmotor', // høy akselrate via gir, sterke eksos-modulerte tonaler
    'ukjent',
]);
export const HydrofonSignatur = z.object({
    tonaler: z.array(TonalKomponent).max(MAX_TONALER).default([]),
    demon: DemonEstimat.optional(),
    klasse: HydrofonKlasse,
    /**
     * 0–1 på KLASSEN. Hold den lav og vær ærlig om hvorfor:
     * kjeden er akselrate → antall blader → propelltype → fartøystype, og hvert
     * ledd er en ANTAKELSE, ikke en måling. Samme bladerate kan komme fra en
     * 3-blads påhengsmotor på 2500 rpm og en 5-blads arbeidsbåt på 1500 rpm.
     * Lasting/vær/fart endrer i tillegg spekteret på samme fartøy fra time til
     * time. En klassekonfidens over ~0,6 fra passiv akustikk ALENE er nesten
     * alltid overmot; over 0,6 skal det ligge fusjon (radar/AIS/kamera) bak.
     */
    klasseKonfidens: z.number().min(0).max(1),
    /** Andre klasser som passer nesten like godt (se kommentar over enum-en). */
    alternativeKlasser: z.array(HydrofonKlasse).max(5).default([]),
    /** Hvorfor klassen ble satt. SKAL vises i UI ved siden av klassen — en klasse
     *  uten begrunnelse er en påstand operatøren ikke kan etterprøve. */
    begrunnelse: z.string().max(400).optional(),
});
// ── Deteksjon ───────────────────────────────────────────────────────────────
/** Om nivået er rms eller topp. SEL har ANNEN enhet og eget felt. */
export const NivaType = z.enum(['rms', 'topp']);
export const TersbandNiva = z.object({
    /** Nominell senterfrekvens for 1/3-oktavbåndet (Hz). Forholdet er 2^(1/3). */
    senterHz: z.number().positive().max(200000),
    nivaDbRe1uPa: z.number(),
});
// Feltene som er felles for begge deteksjonsmodi. Skrevet som et objekt-literal
// og spredt inn i begge union-medlemmene fordi z.discriminatedUnion krever rene
// ZodObject-medlemmer (ingen .merge()-kjeder med effekter).
const deteksjonFelles = {
    deteksjonId: z.string().min(1).max(64),
    postId: z.string().min(1).max(64),
    /**
     * Ankomsttid, ms siden epoch, MED DESIMALER TILLATT. Heltalls-ms er ikke godt
     * nok for TDOA: 1 ms ≈ 1,5 m, så kvantiseringen alene setter gulvet for
     * posisjonsnøyaktigheten. Broen skal sende brøkdeler når den har dem.
     */
    ankomstMs: z.number(),
    /** Hvor lenge hendelsen varte (ms). Et forbipasserende fartøy varer minutter;
     *  en enkelt transient (slag, sprengning) millisekunder. */
    varighetMs: z.number().min(0).optional(),
    /** Analysebåndet nivåene gjelder for. PÅKREVD: «120 dB» uten bånd kan ikke
     *  sammenlignes med noe. */
    bandFraHz: z.number().min(0),
    bandTilHz: z.number().positive(),
    /** Bredbåndsnivå (mottatt) i analysebåndet, dB re 1 µPa. */
    bredbandNivaDbRe1uPa: z.number(),
    nivaType: NivaType.default('rms'),
    /** Målt bakgrunn i SAMME bånd under deteksjonen — grunnlaget for SNR. */
    stoyGulvDbRe1uPa: z.number().optional(),
    /** SNR i dB om broen regner den. Ellers utledes den av de to over. */
    snrDb: z.number().optional(),
    /** Lydeksponeringsnivå. EGET FELT fordi enheten er en ANNEN: dB re 1 µPa²s. */
    selDbRe1uPa2s: z.number().optional(),
    /** 1/3-oktav-spekter. Standardformen å rapportere marin støy i, og det som
     *  gjør at to poster/to sensorer kan sammenlignes i det hele tatt. */
    tersband: z.array(TersbandNiva).max(MAX_TERSBAND).default([]),
    signatur: HydrofonSignatur.optional(),
    /** Fritekst til operatør (f.eks. «lyttet av vakt kl 0412, hørtes som RIB»). */
    merknad: z.string().max(400).optional(),
};
/**
 * Peiling fra et array. Lagrer BARE relativ peiling — sann peiling utledes med
 * postens `referanseretningDeg` (`peilingSann()`). Én kilde til sannhet: hadde vi
 * lagret både relativ og sann kunne de divergert, og da er det ikke mulig å vite
 * hvilken som er feil.
 */
export const AkustiskPeiling = z.object({
    /** Peiling relativ til array-rammens +x-akse, grader 0–360 medsols. */
    relativDeg: z.number().min(0).max(360),
    /**
     * 1σ i grader. PÅKREVD, ikke valgfri: en peiling uten usikkerhet kan ikke
     * krysspeiles (feilellipsen blir udefinert) og forfører operatøren til å tro
     * at strålen er en tynn linje. Fysisk gulv er strålebredden, som skalerer med
     * λ/L — et 2 m array på 200 Hz (λ=7,4 m) har titalls grader oppløsning.
     */
    usikkerhetDeg: z.number().positive().max(90),
    /**
     * Har broen/operatøren AVKLART siden ved en uavhengig kilde (bevegelse over
     * tid, andre post, radar)? Uten avklaring gjelder array-formens tvetydighet.
     */
    sideAvklart: z.boolean().default(false),
    /** Konusvinkel fra array-aksen, om formen kan måle den (grader 0–180). */
    konusvinkelDeg: z.number().min(0).max(180).optional(),
    metode: z.enum(['forsinkelse-og-sum', 'music', 'krysskorrelasjon', 'vektorsensor', 'annet']),
});
/**
 * Deteksjon fra ÉN ENKELT HYDROFON. Legg merke til hva som IKKE er her: det
 * finnes ikke noe peilingsfelt, og det er hele poenget. En omnidireksjonell
 * trykkhydrofon gir «noe er der, med dette nivået, i dette båndet» — retning er
 * fysisk utilgjengelig. Skal du ha retning: bruk array, eller TDOA over flere
 * poster.
 */
export const HydrofonDeteksjonEnkelt = z.object({
    modus: z.literal('enkelt'),
    ...deteksjonFelles,
});
/** Deteksjon fra et array — peiling er PÅKREVD (ellers er det ikke et array). */
export const HydrofonDeteksjonArray = z.object({
    modus: z.literal('array'),
    peiling: AkustiskPeiling,
    ...deteksjonFelles,
});
export const HydrofonDeteksjonSkjema = z.discriminatedUnion('modus', [
    HydrofonDeteksjonEnkelt,
    HydrofonDeteksjonArray,
]);
/** Parse én deteksjon. Null ved ugyldig (aldri kast) — som parseSparkControl. */
export function parseHydrofonDeteksjon(raw) {
    const p = HydrofonDeteksjonSkjema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Parse én post. Null ved ugyldig. */
export function parseHydrofonPost(raw) {
    const p = HydrofonPostSkjema.safeParse(raw);
    return p.success ? p.data : null;
}
// ── Wire-konvolutt ──────────────────────────────────────────────────────────
export const HYDROFON_KANAL = 'hydrofon';
export const HydrofonMelding = z.discriminatedUnion('type', [
    z.object({ type: z.literal('post'), post: HydrofonPostSkjema }),
    z.object({ type: z.literal('deteksjon'), deteksjon: HydrofonDeteksjonSkjema }),
    z.object({
        type: z.literal('status'),
        postId: z.string().min(1),
        /** Sanntids støygulv — operatøren må se når posten er «døv» av sjøstøy. */
        stoyGulvDbRe1uPa: z.number().optional(),
        tsMs: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('borte'),
        postId: z.string().min(1),
        /** Hvorfor kontakten med posten falt. Vises, aldri skjult (som radar.ts). */
        arsak: z.string(),
    }),
]);
// ── Små hjelpere ────────────────────────────────────────────────────────────
function norm360(d) {
    return ((d % 360) + 360) % 360;
}
/**
 * Kan nivåene fra denne posten leses som ABSOLUTTE dB re 1 µPa? Uten gyldig
 * kalibrering er tallet bare sammenlignbart med postens eget støygulv, og UI-et
 * må si «relativt» — ellers sammenligner operatøren epler og pærer mellom poster.
 */
export function nivaErAbsolutt(post) {
    const s = post.kalibrering.status;
    return (s === 'kalibrert' || s === 'fabrikkverdi') && post.kalibrering.folsomhetDbReV1uPa !== undefined;
}
/**
 * Har denne array-formen en peilingstvetydighet vi ikke kan løse internt?
 * `lineær` → speiling om aksen (babord/styrbord). `plan` → for/bak.
 * `volum`/`vektor` → entydig.
 */
export function harSideTvetydighet(geom) {
    return geom.form === 'lineær' || geom.form === 'plan';
}
/**
 * Relativ peiling → sann peiling, med tvetydighet og totalt usikkerhetsbudsjett.
 * Returnerer null hvis posten ikke er et array — for da FINNES ingen peiling, og
 * vi nekter å oppfinne en.
 */
export function peilingSann(post, peiling) {
    if (post.type !== 'array' || !post.array)
        return null;
    const geom = post.array;
    const sannDeg = norm360(geom.referanseretningDeg + peiling.relativDeg);
    const tvetydig = harSideTvetydighet(geom) && !peiling.sideAvklart;
    const advarsler = [];
    let speilSannDeg;
    if (tvetydig) {
        if (geom.form === 'lineær') {
            // Speiling om +x-aksen: samme konusvinkel, motsatt side → rel' = 360 − rel.
            speilSannDeg = norm360(geom.referanseretningDeg - peiling.relativDeg);
            advarsler.push('lineært array: babord/styrbord kan ikke skilles — to like gyldige peilinger');
        }
        else {
            // For/bak-tvetydighet for plan array krever planets normal, som vi ikke
            // bærer i kontrakten ennå. Da sier vi det, i stedet for å regne feil.
            advarsler.push('plan array uten baffel har for/bak-tvetydighet; speilpeilingens geometri er IKKE modellert her (UVERIFISERT)');
        }
    }
    if (geom.geometriKilde !== 'oppmålt') {
        advarsler.push(`array-geometrien er «${geom.geometriKilde}», ikke oppmålt — peilingen kan ha systematisk skjevhet`);
    }
    const totalUsikkerhetDeg = Math.sqrt(peiling.usikkerhetDeg ** 2 + geom.referanseretningUsikkerhetDeg ** 2);
    return { sannDeg, speilSannDeg, tvetydig, totalUsikkerhetDeg, advarsler };
}
/**
 * Antall blader = bladerate / akselrate. Ser trivielt ut; er det ikke:
 *  - DEMON gir ofte HARMONISKE av bladeraten, så «bladerate» kan være 2× eller
 *    3× den sanne → forholdet blir 6 eller 9 i stedet for 3.
 *  - Med to aksler blandes to linjesett, og forholdet blir meningsløst.
 *  - Ved lav kavitasjon er akselrate-linja svak og estimeres feil.
 * Derfor: vi returnerer BARE et tall når forholdet ligger nær et heltall i
 * området 2–7 blader (fysisk vanlig for overflatefartøy). Ellers `undefined` —
 * som betyr «vi vet ikke», ikke «0 blader».
 */
export function estimerAntallBlader(bladerateHz, akselrateHz, toleranse = 0.15) {
    if (!bladerateHz || !akselrateHz || akselrateHz <= 0)
        return undefined;
    const forhold = bladerateHz / akselrateHz;
    const n = Math.round(forhold);
    if (n < 2 || n > 7)
        return undefined;
    if (Math.abs(forhold - n) > toleranse)
        return undefined;
    return n;
}
/**
 * Grov klasse fra DEMON + bredbåndsnivå.
 *
 * ALLE TERSKLER HER ER UVERIFISERT: de er plukket fra litteraturens typiske
 * verdier for småbåt (påhengsmotor med ~2:1 gir gir propellrate i titalls Hz,
 * deplasementfartøy noen få Hz) og er IKKE målt i Oslofjorden. De skal
 * kalibreres mot egne opptak før de brukes til noe som utløser handling.
 *
 * Konfidensen er hardt begrenset til 0,5 med vilje. Se kommentaren på
 * `HydrofonSignatur.klasseKonfidens`: kjeden rate → blader → fartøystype er en
 * kjede av antakelser. Funksjonen returnerer også `alternativeKlasser`, fordi
 * «planende» og «påhengsmotor» typisk er SAMME båt.
 */
export function grovKlasseFraDemon(demon, bredbandNivaDbRe1uPa) {
    const ukjent = (hvorfor) => ({
        klasse: 'ukjent',
        klasseKonfidens: 0.1,
        alternativeKlasser: [],
        begrunnelse: hvorfor,
    });
    if (!demon || demon.akselrateHz === undefined) {
        return ukjent('ingen akselrate fra DEMON — bare bredbåndsenergi, ingen holdepunkt for klasse');
    }
    if (demon.konfidens < 0.3) {
        return ukjent(`DEMON-linjene er for svake (konfidens ${demon.konfidens.toFixed(2)}) til å tolke rate`);
    }
    const a = demon.akselrateHz;
    const blader = estimerAntallBlader(demon.bladerateHz, a);
    const hoyNiva = (bredbandNivaDbRe1uPa ?? -Infinity) >= 130; // UVERIFISERT terskel
    if (a < 12) {
        // 12 Hz ≈ 720 rpm på propellen. Under det er man i deplasement/arbeidsbåt.
        return {
            klasse: 'sakte-fartøy',
            klasseKonfidens: 0.45,
            alternativeKlasser: ['ukjent'],
            begrunnelse: `akselrate ${a.toFixed(1)} Hz (~${Math.round(a * 60)} rpm) er lav — deplasementfart${blader ? `, ${blader} blader antatt` : ''}`,
        };
    }
    if (a <= 70) {
        // Her ligger BÅDE planende skrog og påhengsmotor. De er ikke til å skille
        // akustisk, fordi det normalt er samme båt. Vi rangerer på nivå/kavitasjon.
        if (demon.kavitasjon === true && hoyNiva) {
            return {
                klasse: 'planende-fartøy',
                klasseKonfidens: 0.5,
                alternativeKlasser: ['påhengsmotor', 'vannjet'],
                begrunnelse: `akselrate ${a.toFixed(1)} Hz + kraftig kavitasjon + høyt bredbåndsnivå ⇒ planende. Fremdriften kan like godt være påhengsmotor — klassene overlapper`,
            };
        }
        return {
            klasse: 'påhengsmotor',
            klasseKonfidens: 0.35,
            alternativeKlasser: ['planende-fartøy', 'sakte-fartøy'],
            begrunnelse: `akselrate ${a.toFixed(1)} Hz passer girdrevet påhengsmotor, men uten sterk kavitasjon kan det være samme motor i marsjfart`,
        };
    }
    // Over ~70 Hz akselrate er en fri propell uvanlig; dyse/impeller (vannjet) har
    // høy rate og mange blader, og gir svake diskrete linjer. MEN: en feiltolket
    // harmonisk gir samme bilde. Derfor lav konfidens.
    return {
        klasse: 'vannjet',
        klasseKonfidens: 0.3,
        alternativeKlasser: ['påhengsmotor', 'ukjent'],
        begrunnelse: `akselrate ${a.toFixed(1)} Hz er høy for fri propell — kan være dyseanlegg, men like sannsynlig en feiltolket harmonisk av en lavere rate`,
    };
}
/**
 * Lydhastighet i sjøvann, Mackenzie (1981) ni-leddsformel.
 *
 * HVORFOR DETTE IKKE ER EN KONSTANT: c varierer ~1450–1540 m/s med temperatur,
 * saltholdighet og dybde. 1 % feil i c gir ~1 % feil i alle avstander TDOA
 * regner ut — på 2 km er det 20 m. Det er ofte STØRRE enn tidsfeilen. Derfor er
 * `lydhastighetMs` en PÅKREVD parameter i lokaliseringen, aldri en default.
 *
 * Oslofjorden er i tillegg brakk: overflatelaget kan ligge langt under 30 psu,
 * altså UTENFOR formelens gyldighet (30–40 psu). Da flagges det, i stedet for å
 * returnere et tall som ser presist ut. Det riktige er en CTD-profil.
 *
 * Referanseverdi for kontroll: T=25 °C, S=35 psu, D=1000 m → 1550,744 m/s.
 */
export function lydhastighetMackenzie(tempC, salinitetPsu, dybdeM) {
    const T = tempC;
    const S = salinitetPsu;
    const D = dybdeM;
    const ms = 1448.96 +
        4.591 * T -
        5.304e-2 * T ** 2 +
        2.374e-4 * T ** 3 +
        1.34 * (S - 35) +
        1.63e-2 * D +
        1.675e-7 * D ** 2 -
        1.025e-2 * T * (S - 35) -
        7.139e-13 * T * D ** 3;
    const utenfor = T < 0 || T > 30 || S < 30 || S > 40 || D < 0 || D > 8000;
    return {
        ms,
        utenforGyldighet: utenfor,
        merknad: utenfor
            ? 'utenfor Mackenzie-gyldighet (T 0–30 °C, S 30–40 psu, D 0–8000 m) — brakkvann/isvann krever CTD-profil, ikke formel'
            : undefined,
    };
}
/**
 * Ekvirektangulært lokalplan om et referansepunkt. Samme jordradius som geo.ts
 * (6 371 km) slik at plan-avstand og `haversineM` ikke divergerer — vi har blitt
 * bitt av to litt ulike jordmodeller før. Gyldig over noen få km; over det (og
 * nær polene) er feilen ikke lenger neglisjerbar, derfor gate på breddegrad.
 */
const M_PER_DEG = (Math.PI / 180) * 6_371_000;
function lagPlan(ref) {
    const mPerDegLon = M_PER_DEG * Math.cos((ref.lat * Math.PI) / 180);
    return {
        til: (p) => ({
            x: (p.lon - ref.lon) * mPerDegLon,
            y: (p.lat - ref.lat) * M_PER_DEG,
        }),
        fra: (q) => ({
            lat: ref.lat + q.y / M_PER_DEG,
            lon: ref.lon + q.x / mPerDegLon,
        }),
    };
}
/**
 * Grov-til-fin rutenettsøk etter minimum av `kost`. Valgt framfor en lukket
 * TDOA-løsning (Schau–Robinson/Chan–Ho) med vilje:
 *  - de lukkede løsningene krever N ≥ 4 og oppfører seg dårlig ved slett
 *    geometri, mens vi ofte har nøyaktig 3 poster,
 *  - rutenettsøk finner det GLOBALE minimumet, så vi kan lete etter en ANDRE
 *    løsning og faktisk oppdage tvetydighet (3 kolineære hydrofoner har en
 *    eksakt speilløsning — en matriseinvertering skjuler det),
 *  - kostnaden er ~10⁴ avstandsberegninger. Det er ingenting.
 * `unnta` lar oss lete utenom en disk rundt en kjent løsning.
 */
function sokBoks(kost, boks, unnta) {
    const GROVT = 48;
    const FINT = 20;
    const NIVAER = 8;
    let { x0, x1, y0, y1 } = boks;
    let best = { x: (x0 + x1) / 2, y: (y0 + y1) / 2, k: Infinity };
    for (let niva = 0; niva < NIVAER; niva++) {
        const n = niva === 0 ? GROVT : FINT;
        let lokal = { x: best.x, y: best.y, k: Infinity };
        for (let i = 0; i <= n; i++) {
            const x = x0 + ((x1 - x0) * i) / n;
            for (let j = 0; j <= n; j++) {
                const y = y0 + ((y1 - y0) * j) / n;
                if (unnta && Math.hypot(x - unnta.x, y - unnta.y) < unnta.r)
                    continue;
                const k = kost({ x, y });
                if (k < lokal.k)
                    lokal = { x, y, k };
            }
        }
        if (!Number.isFinite(lokal.k))
            return best; // alt ekskludert av `unnta`
        best = lokal;
        // Krymp til ±2 cellebredder rundt beste celle. Overlappet er med vilje: uten
        // det kan minimumet ligge rett utenfor kanten av den nye boksen.
        const dx = ((x1 - x0) / n) * 2;
        const dy = ((y1 - y0) / n) * 2;
        x0 = best.x - dx;
        x1 = best.x + dx;
        y0 = best.y - dy;
        y1 = best.y + dy;
    }
    return best;
}
/**
 * TDOA-lokalisering (hyperbolsk) fra ≥3 TIDSSYNKRONISERTE hydrofoner.
 *
 * HVORFOR TDOA OG IKKE BARE KRYSSPEILING: krysspeiling krever at hver post har
 * et array med kjent referanseretning — dyrt, og tvetydig hvis arrayet er
 * lineært. Realiteten for en spredt kjede av NJORD-poster er billige
 * ENKELThydrofoner. De kan ikke peile hver for seg, men med felles klokke bærer
 * ankomsttidsforskjellene all informasjonen: hver differanse definerer en
 * hyperbel, og tre poster skjærer hyperblene i et punkt. `krysspeilingAkustisk()`
 * finnes i tillegg, for postene som faktisk HAR array.
 *
 * FEILKILDER, i den rekkefølgen de faktisk plager oss:
 *  1. klokkesynk — 1 ms ≈ 1,5 m, og NTP gir titalls ms,
 *  2. lydhastighet — 1 % ≈ 1 % av avstanden, se `lydhastighetMackenzie()`,
 *  3. geometri (GDOP) — mål utenfor basislinja er dårlig bestemt,
 *  4. flerveisutbredelse — i grunt vann kan første ankomst være en bunn/overflate-
 *     refleksjon, som gir for LANG gangtid. Vi kan ikke oppdage det her; det er
 *     grunnen til at `restfeilM` alltid skal vises til operatøren.
 * Vi regner 2D horisontalt: kilden antas å være en OVERFLATEkilde. Skråavstanden
 * til en hydrofon på dybde d underestimeres med d²/2R (på 500 m avstand og 30 m
 * dybde: ~0,9 m). Det er under de andre feilene, men ikke null.
 */
export function tdoaLokalisering(obs, p) {
    const advarsler = [];
    if (!Array.isArray(obs) || obs.length < 3) {
        return {
            ok: false,
            arsak: 'TDOA krever minst 3 poster — 2 poster gir bare én hyperbel (en kurve), ikke et punkt',
        };
    }
    if (obs.length > MAX_POSTER)
        return { ok: false, arsak: `for mange poster (maks ${MAX_POSTER})` };
    const c = p.lydhastighetMs;
    if (!Number.isFinite(c) || c < 1350 || c > 1650) {
        return { ok: false, arsak: 'lydhastighet mangler eller er utenfor fysisk rimelig område 1350–1650 m/s' };
    }
    if (c < 1450 || c > 1540) {
        advarsler.push(`lydhastighet ${c.toFixed(1)} m/s ligger utenfor vanlig kystintervall 1450–1540 m/s — kontroller CTD/antakelse`);
    }
    const sett = new Set();
    for (const o of obs) {
        if (sett.has(o.post.postId))
            return { ok: false, arsak: `postId «${o.post.postId}» er med flere ganger` };
        sett.add(o.post.postId);
        if (!Number.isFinite(o.ankomstMs))
            return { ok: false, arsak: `ugyldig ankomsttid for ${o.post.postId}` };
        if (Math.abs(o.post.lat) > 85)
            return { ok: false, arsak: 'lokalplanet er ikke gyldig over 85° bredde' };
        if (o.post.tidssync === 'ingen') {
            return {
                ok: false,
                arsak: `post ${o.post.postId} er ikke tidssynkronisert — TDOA uten felles klokke er ikke unøyaktig, den er meningsløs`,
            };
        }
        if (o.post.tidssync === 'ntp') {
            advarsler.push(`post ${o.post.postId} er NTP-synket (titalls ms ⇒ titalls meter) — bruk GPS/PPS for brukbar TDOA`);
        }
        if (o.post.type === 'array') {
            advarsler.push(`post ${o.post.postId} er et array; peilingen dens brukes IKKE av TDOA (se krysspeilingAkustisk)`);
        }
    }
    if (obs.every((o) => Number.isInteger(o.ankomstMs))) {
        advarsler.push('alle ankomsttider er heltalls millisekunder — kvantiseringen alene setter et gulv på ~1,5 m per ms');
    }
    // Fysisk mulighetssjekk: en ankomstdifferanse kan ALDRI svare til mer enn
    // avstanden mellom de to postene. Bryter den det, er det klokkefeil, feil
    // parring av deteksjoner, eller to ulike kilder — ikke noe å lokalisere.
    const sigmaT = p.tidsusikkerhetMs ?? 0.5;
    for (let i = 0; i < obs.length; i++) {
        for (let j = i + 1; j < obs.length; j++) {
            const a = obs[i];
            const b = obs[j];
            const basis = haversineM(a.post, b.post);
            if (basis < 1) {
                return { ok: false, arsak: `postene ${a.post.postId} og ${b.post.postId} står i praksis på samme punkt (${basis.toFixed(2)} m)` };
            }
            const sT = Math.max(a.post.klokkeusikkerhetMs ?? sigmaT, b.post.klokkeusikkerhetMs ?? sigmaT);
            const rd = Math.abs(((a.ankomstMs - b.ankomstMs) / 1000) * c);
            const slakk = 3 * sT * 1e-3 * c + 1; // 3σ i tid, pluss 1 m for planfeil
            if (rd > basis + slakk) {
                return {
                    ok: false,
                    arsak: `tidsforskjellen mellom ${a.post.postId} og ${b.post.postId} svarer til ${rd.toFixed(0)} m, men basislinja er bare ${basis.toFixed(0)} m — fysisk umulig (klokkefeil eller feilparret deteksjon)`,
                };
            }
        }
    }
    // Lokalplan om første post.
    const plan = lagPlan({ lat: obs[0].post.lat, lon: obs[0].post.lon });
    const punkter = obs.map((o) => plan.til({ lat: o.post.lat, lon: o.post.lon }));
    const tider = obs.map((o) => o.ankomstMs);
    // Kostfunksjon: referansepost er den som HØRTE FØRST (nærmest kilden) — det gir
    // ikke-negative avstandsdifferanser og best numerisk oppførsel.
    const lagKost = (t, cc) => {
        let ref = 0;
        for (let i = 1; i < t.length; i++)
            if (t[i] < t[ref])
                ref = i;
        const rp = punkter[ref];
        const andre = [];
        for (let i = 0; i < t.length; i++) {
            if (i === ref)
                continue;
            andre.push({ p: punkter[i], rd: ((t[i] - t[ref]) / 1000) * cc });
        }
        return (s) => {
            const dRef = Math.hypot(s.x - rp.x, s.y - rp.y);
            let sum = 0;
            for (const a of andre) {
                const d = Math.hypot(s.x - a.p.x, s.y - a.p.y);
                const r = d - dRef - a.rd;
                sum += r * r;
            }
            return sum / andre.length;
        };
    };
    const margin = p.sokemarginM ?? 5000;
    const xs = punkter.map((q) => q.x);
    const ys = punkter.map((q) => q.y);
    const bboks = {
        x0: Math.min(...xs) - margin,
        x1: Math.max(...xs) + margin,
        y0: Math.min(...ys) - margin,
        y1: Math.max(...ys) + margin,
    };
    const kost = lagKost(tider, c);
    const best = sokBoks(kost, bboks);
    const restfeilM = Math.sqrt(best.k);
    // Tvetydighet: let etter det beste minimumet UTENFOR en disk rundt løsningen.
    // Med 3 kolineære hydrofoner er speilbildet om linja en EKSAKT like god
    // løsning; med en trekant er den nest beste mye dårligere.
    const UNNTA_R = 250; // m — større enn realistisk restfeil, mindre enn en speiling
    const alt = sokBoks(kost, bboks, { x: best.x, y: best.y, r: UNNTA_R });
    const altRest = Math.sqrt(alt.k);
    const tidsSlakkM = c * (sigmaT * 1e-3);
    const tvetydig = Number.isFinite(alt.k) &&
        Math.hypot(alt.x - best.x, alt.y - best.y) >= UNNTA_R &&
        altRest <= Math.max(restfeilM * 1.5, restfeilM + tidsSlakkM);
    // Sensitivitet → grov cepM. Vi perturberer én feilkilde om gangen og måler hvor
    // langt løsningen flytter seg. Ikke et konfidensintervall; se feltkommentaren.
    let cepM;
    if (!p.utenUsikkerhet) {
        const litenBoks = () => {
            const r = Math.max(500, 10 * restfeilM);
            return { x0: best.x - r, x1: best.x + r, y0: best.y - r, y1: best.y + r };
        };
        let maks = 0;
        const dc = p.lydhastighetUsikkerhetMs ?? 15;
        for (const cc of [c - dc, c + dc]) {
            const q = sokBoks(lagKost(tider, cc), litenBoks());
            maks = Math.max(maks, Math.hypot(q.x - best.x, q.y - best.y));
        }
        for (let i = 0; i < tider.length; i++) {
            const sT = obs[i].post.klokkeusikkerhetMs ?? sigmaT;
            for (const tegn of [-1, 1]) {
                const t2 = tider.slice();
                t2[i] = t2[i] + tegn * sT;
                const q = sokBoks(lagKost(t2, c), litenBoks());
                maks = Math.max(maks, Math.hypot(q.x - best.x, q.y - best.y));
            }
        }
        cepM = Math.round(maks * 10) / 10;
    }
    const utenfor = best.x < Math.min(...xs) || best.x > Math.max(...xs) || best.y < Math.min(...ys) || best.y > Math.max(...ys);
    if (utenfor) {
        advarsler.push('løsningen ligger utenfor postenes basislinje — dårlig betinget geometri (GDOP), avstanden er svakt bestemt');
    }
    if (tvetydig) {
        advarsler.push('to løsninger passer like godt (kolineære poster?) — bruk en fjerde post eller en annen sensor for å velge side');
    }
    const pos = plan.fra(best);
    const altPos = plan.fra(alt);
    return {
        ok: true,
        losning: {
            lat: pos.lat,
            lon: pos.lon,
            restfeilM,
            cepM,
            metode: 'tdoa',
            bidrag: obs.map((o) => o.post.postId),
            utenforBasislinje: utenfor,
            tvetydig,
            alternativ: tvetydig ? { lat: altPos.lat, lon: altPos.lon, restfeilM: altRest } : undefined,
            advarsler,
            lydhastighetMs: c,
        },
    };
}
/**
 * Krysspeiling fra ≥2 ARRAY-poster. Merk at lydhastigheten IKKE inngår: en
 * krysspeiling er ren geometri på retninger. Det er faktisk en fordel — den
 * arver ikke lydhastighetsfeilen. Til gjengjeld arver den strålebredden, som for
 * et kort array på lav frekvens er stygg (λ/L), og hele babord/styrbord-
 * tvetydigheten for lineære array.
 */
export function krysspeilingAkustisk(obs, param = {}) {
    const advarsler = [];
    if (!Array.isArray(obs) || obs.length < 2)
        return { ok: false, arsak: 'krysspeiling krever minst 2 array-poster' };
    if (obs.length > MAX_POSTER)
        return { ok: false, arsak: `for mange poster (maks ${MAX_POSTER})` };
    const maksAvstand = param.maksAvstandM ?? 20000;
    const minKutt = param.minKuttvinkelDeg ?? 15;
    const plan = lagPlan({ lat: obs[0].post.lat, lon: obs[0].post.lon });
    const straler = [];
    for (const o of obs) {
        if (o.deteksjon.modus !== 'array') {
            return {
                ok: false,
                arsak: `post ${o.post.postId} har en enkelt-hydrofon-deteksjon uten peiling — bruk tdoaLokalisering() for enkelthydrofoner`,
            };
        }
        const sp = peilingSann(o.post, o.deteksjon.peiling);
        if (!sp)
            return { ok: false, arsak: `post ${o.post.postId} mangler array-geometri (referanseretning) — peilingen kan ikke gjøres sann` };
        if (sp.tvetydig) {
            // Vi bruker HOVEDpeilingen, men sier det høyt. Å prøve alle kombinasjoner av
            // speilpeilinger ville gitt flere «løsninger» uten grunnlag for å velge.
            advarsler.push(`peilingen fra ${o.post.postId} er tvetydig (${sp.advarsler[0] ?? 'array-form'}) — hovedpeilingen brukes, siden MÅ avklares`);
        }
        for (const a of sp.advarsler)
            if (!advarsler.includes(a))
                advarsler.push(`${o.post.postId}: ${a}`);
        const brg = (sp.sannDeg * Math.PI) / 180;
        straler.push({
            postId: o.post.postId,
            p: plan.til({ lat: o.post.lat, lon: o.post.lon }),
            ux: Math.sin(brg), // øst-komponent
            uy: Math.cos(brg), // nord-komponent
            sigmaDeg: sp.totalUsikkerhetDeg,
        });
    }
    const skjaeringer = [];
    let forkastet = 0;
    for (let i = 0; i < straler.length; i++) {
        for (let j = i + 1; j < straler.length; j++) {
            const a = straler[i];
            const b = straler[j];
            const kryss = a.ux * b.uy - a.uy * b.ux;
            // Kuttvinkel: |sin θ| = |kryss| for enhetsvektorer.
            const kuttDeg = (Math.asin(Math.min(1, Math.abs(kryss))) * 180) / Math.PI;
            if (kuttDeg < minKutt) {
                forkastet++;
                advarsler.push(`${a.postId}×${b.postId}: kuttvinkel ${kuttDeg.toFixed(1)}° er under ${minKutt}° — skjæringen er en lang ellipse, ikke et punkt`);
                continue;
            }
            const dx = b.p.x - a.p.x;
            const dy = b.p.y - a.p.y;
            const ta = (dx * b.uy - dy * b.ux) / kryss;
            const tb = (dx * a.uy - dy * a.ux) / kryss;
            if (ta <= 0 || tb <= 0) {
                // Skjæringen ligger BAK minst én peiling. Da er den ikke en løsning, den
                // er et artefakt av å behandle peilinger som uendelige linjer.
                forkastet++;
                advarsler.push(`${a.postId}×${b.postId}: skjæringen ligger bak en av peilingene — forkastet`);
                continue;
            }
            if (ta > maksAvstand || tb > maksAvstand) {
                forkastet++;
                advarsler.push(`${a.postId}×${b.postId}: skjæringen ligger ${Math.round(Math.max(ta, tb))} m ute, over maks ${maksAvstand} m`);
                continue;
            }
            // Tverr-avstandsfeil ved skjæringen fra hver stråles vinkelusikkerhet.
            const ea = ta * Math.tan((a.sigmaDeg * Math.PI) / 180);
            const eb = tb * Math.tan((b.sigmaDeg * Math.PI) / 180);
            skjaeringer.push({
                q: { x: a.p.x + ta * a.ux, y: a.p.y + ta * a.uy },
                feilM: Math.hypot(ea, eb),
            });
        }
    }
    if (skjaeringer.length === 0) {
        return {
            ok: false,
            arsak: `ingen brukbar skjæring (${forkastet} par forkastet: bak peiling, for liten kuttvinkel eller for langt ute)`,
        };
    }
    const mx = skjaeringer.reduce((s, v) => s + v.q.x, 0) / skjaeringer.length;
    const my = skjaeringer.reduce((s, v) => s + v.q.y, 0) / skjaeringer.length;
    const spredningM = Math.sqrt(skjaeringer.reduce((s, v) => s + (v.q.x - mx) ** 2 + (v.q.y - my) ** 2, 0) / skjaeringer.length);
    const feilRms = Math.sqrt(skjaeringer.reduce((s, v) => s + v.feilM ** 2, 0) / skjaeringer.length);
    if (spredningM > feilRms && skjaeringer.length > 1) {
        advarsler.push(`skjæringene spriker ${spredningM.toFixed(0)} m, mer enn peilingsusikkerheten tilsier (${feilRms.toFixed(0)} m) — minst én peiling er feil, eller det er to fartøy`);
    }
    const xs = straler.map((s) => s.p.x);
    const ys = straler.map((s) => s.p.y);
    const utenfor = mx < Math.min(...xs) || mx > Math.max(...xs) || my < Math.min(...ys) || my > Math.max(...ys);
    const pos = plan.fra({ x: mx, y: my });
    return {
        ok: true,
        losning: {
            lat: pos.lat,
            lon: pos.lon,
            restfeilM: spredningM,
            cepM: Math.round(Math.max(feilRms, spredningM) * 10) / 10,
            metode: 'krysspeiling',
            bidrag: straler.map((s) => s.postId),
            utenforBasislinje: utenfor,
            tvetydig: false,
            spredningM,
            advarsler,
            // Ren geometri — lydhastigheten inngår ikke. NaN ville vært vondt for
            // nedstrøms formatering, så vi sender 0 og sier hvorfor i advarslene.
            lydhastighetMs: 0,
        },
    };
}
// ── Track-adaptere ──────────────────────────────────────────────────────────
/**
 * SIDC-er. UVERIFISERT: valgt etter APP-6/2525B-logikk (posisjon 2 = tilhørighet,
 * posisjon 3 = kampdimensjon 'S' = sjøoverflate) og speiler den 11-tegns
 * legacy-formen som resten av SKADE bruker (se adapters.ts). Skal kontrolleres
 * mot symbolbiblioteket før produksjon.
 */
const SIDC_UKJENT_OVERFLATE = 'SUSP-------';
const SIDC_VENNLIG_SENSOR = 'SFGPEWS----'; // samme som SPARK-node i adapters.ts
/** Hydrofonposten som vennlig sensor-spor. */
export function hydrofonPostTilTrack(post, tsMs = 0) {
    return {
        id: `hydrofon:${post.postId}`,
        kind: 'sensor',
        affiliation: 'friend',
        lat: post.lat,
        lon: post.lon,
        sidc: SIDC_VENNLIG_SENSOR,
        label: `HYDROFON ${post.navn}`,
        tsMs,
        source: `hydrofon:${post.postId}`,
        extra: {
            type: post.type,
            dybde: `${post.dybdeM} m`,
            kalibrering: post.kalibrering.status,
            nivå: nivaErAbsolutt(post) ? 'absolutt (dB re 1 µPa)' : 'RELATIVT — ikke kalibrert',
            tidssync: post.tidssync,
            ...(post.array ? { array: `${post.array.form}, ${post.array.elementer.length} el, ref ${post.array.referanseretningDeg}°` } : {}),
            ...(post.stoyGulvDbRe1uPa !== undefined ? { støygulv: `${post.stoyGulvDbRe1uPa.toFixed(1)} dB re 1 µPa` } : {}),
        },
    };
}
/**
 * Deteksjon → Track. DET ÆRLIGE VALGET, og hvorfor:
 *
 * Passiv akustikk gir ingen avstand. Én post kan derfor ikke gi en posisjon —
 * heller ikke et array, som bare gir retning. Tre alternativer fantes:
 *   (a) projisere langs peilingen på en antatt avstand → et FALSKT punkt på
 *       kartet som ser like ekte ut som et radarplott. Forkastet: dette er
 *       nøyaktig feilen som får folk til å skyte mot nord.
 *   (b) returnere null → deteksjonen forsvinner fra COP-en. Forkastet: en
 *       akustisk førstedeteksjon er det mest tidskritiske vi har.
 *   (c) plassere sporet på HYDROFONENS posisjon, merket `estimated`, med
 *       avstand eksplisitt «ukjent» og peiling i `extra`. Valgt.
 * `cepM` settes med vilje IKKE: en feilradius rundt hydrofonen ville blitt lest
 * som «målet er innenfor X m av dette punktet», som er usant. Selve retningen
 * tegnes som en STRÅLE med `hydrofonPeilingStraler()`, ikke som et punkt.
 *
 * Tilhørighet er `unknown`, ikke `hostile` (der SPARK-adapteren bruker hostile
 * for skudd): en propell i vannet kan like godt være en fisker eller en ferje.
 * Klassifisering av tilhørighet er en menneskelig beslutning, ikke DEMON sin.
 */
export function hydrofonDeteksjonTilTrack(post, det) {
    const sig = det.signatur;
    const klasse = sig?.klasse ?? 'ukjent';
    const absolutt = nivaErAbsolutt(post);
    const extra = {
        posisjon: 'HYDROFONENS posisjon — ikke målets',
        avstand: 'UKJENT — passiv akustikk måler ikke avstand',
        nivå: `${det.bredbandNivaDbRe1uPa.toFixed(1)} dB re 1 µPa (${det.nivaType})${absolutt ? '' : ' — RELATIVT, posten er ikke kalibrert'}`,
        bånd: `${Math.round(det.bandFraHz)}–${Math.round(det.bandTilHz)} Hz`,
    };
    if (det.snrDb !== undefined)
        extra['snr'] = `${det.snrDb.toFixed(1)} dB`;
    else if (det.stoyGulvDbRe1uPa !== undefined) {
        extra['snr'] = `${(det.bredbandNivaDbRe1uPa - det.stoyGulvDbRe1uPa).toFixed(1)} dB (utledet)`;
    }
    if (det.selDbRe1uPa2s !== undefined)
        extra['sel'] = `${det.selDbRe1uPa2s.toFixed(1)} dB re 1 µPa²s`;
    if (det.modus === 'array') {
        const sp = peilingSann(post, det.peiling);
        if (sp) {
            extra['peiling'] = `${sp.sannDeg.toFixed(1)}° ±${sp.totalUsikkerhetDeg.toFixed(1)}° (1σ)`;
            if (sp.speilSannDeg !== undefined)
                extra['speilpeiling'] = `${sp.speilSannDeg.toFixed(1)}° — siden er IKKE avklart`;
            else if (sp.tvetydig)
                extra['peiling-tvetydig'] = 'ja (se advarsel om array-form)';
            extra['peilemetode'] = det.peiling.metode;
        }
        else {
            // Deteksjonen sier «array», posten sier noe annet. Vi later ikke som.
            extra['peiling'] = 'IKKE BRUKBAR — posten mangler array-geometri';
        }
    }
    else {
        extra['peiling'] = 'INGEN — enkelt hydrofon er omnidireksjonell';
    }
    if (sig) {
        extra['klasse'] = `${klasse} (konfidens ${sig.klasseKonfidens.toFixed(2)})`;
        if (sig.alternativeKlasser.length > 0)
            extra['alternativer'] = sig.alternativeKlasser.join(', ');
        if (sig.begrunnelse)
            extra['begrunnelse'] = sig.begrunnelse;
        if (sig.demon?.akselrateHz !== undefined) {
            extra['akselrate'] = `${sig.demon.akselrateHz.toFixed(1)} Hz (~${Math.round(sig.demon.akselrateHz * 60)} rpm)`;
        }
        if (sig.demon?.bladerateHz !== undefined)
            extra['bladerate'] = `${sig.demon.bladerateHz.toFixed(1)} Hz`;
        if (sig.demon?.antallBladerEstimat !== undefined) {
            extra['blader'] = `${sig.demon.antallBladerEstimat} (UTLEDET av rateforhold, ikke målt)`;
        }
        if (sig.tonaler.length > 0) {
            extra['tonaler'] = sig.tonaler
                .slice(0, 5)
                .map((t) => `${t.frekvensHz.toFixed(0)} Hz/${t.nivaDbRe1uPa.toFixed(0)} dB`)
                .join(', ');
        }
    }
    return {
        id: `akustikk:${post.postId}:${klasse}`, // stabil per post+klasse (ikke ts) → oppdaterer, jf. adapters.ts
        kind: 'detection',
        affiliation: 'unknown',
        lat: post.lat,
        lon: post.lon,
        sidc: SIDC_UKJENT_OVERFLATE,
        label: `AKUSTISK ${klasse.toUpperCase()}`,
        tsMs: Math.round(det.ankomstMs),
        // cepM med vilje utelatt — se funksjonskommentaren.
        confidence: sig?.klasseKonfidens,
        source: `hydrofon:${post.postId}`,
        estimated: true,
        extra,
    };
}
/**
 * Peilingsstråler som GeoJSON. `lengdeM` er en OPERATØRVERDI (antatt
 * deteksjonsrekkevidde), ikke en måling — derfor er den påkrevd parameter og
 * legges i properties, så UI-et kan si «stråle vist til 8 km, antatt». Ved
 * tvetydig lineært array kommer BEGGE strålene, fordi begge er like sanne.
 */
export function hydrofonPeilingStraler(post, det, lengdeM) {
    if (det.modus !== 'array' || lengdeM <= 0)
        return featureCollection([]);
    const sp = peilingSann(post, det.peiling);
    if (!sp)
        return featureCollection([]);
    const fra = { lat: post.lat, lon: post.lon };
    const lag = (deg, type) => {
        const til = project(fra, deg, lengdeM);
        return {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: [
                    [fra.lon, fra.lat],
                    [til.lon, til.lat],
                ],
            },
            properties: {
                postId: post.postId,
                type,
                peilingDeg: deg,
                usikkerhetDeg: sp.totalUsikkerhetDeg,
                antattLengdeM: lengdeM,
            },
        };
    };
    const ut = [lag(sp.sannDeg, 'peiling')];
    if (sp.speilSannDeg !== undefined)
        ut.push(lag(sp.speilSannDeg, 'speilpeiling'));
    return featureCollection(ut);
}
/**
 * Løst punkt (TDOA/krysspeiling) → Track. Her er `estimated` FALSE og `cepM`
 * satt, i motsetning til `hydrofonDeteksjonTilTrack`: dette ER et løst punkt,
 * med en feilradius vi kan forsvare. Skillet er det samme som i adapters.ts
 * mellom «løst punkt» og «estimert (peiling)».
 */
export function akustiskLosningTilTrack(los, tsMs, klasse = 'ukjent') {
    return {
        id: `akustikk-fix:${los.bidrag.join('+')}`,
        kind: 'detection',
        affiliation: 'unknown',
        lat: los.lat,
        lon: los.lon,
        sidc: SIDC_UKJENT_OVERFLATE,
        label: `AKUSTISK FIX ${klasse.toUpperCase()}`,
        tsMs,
        cepM: los.cepM,
        source: `hydrofon:${los.bidrag.join('+')}`,
        estimated: false,
        extra: {
            metode: los.metode,
            restfeil: `${los.restfeilM.toFixed(1)} m`,
            poster: los.bidrag.join(', '),
            ...(los.metode === 'tdoa' ? { lydhastighet: `${los.lydhastighetMs.toFixed(1)} m/s (antatt)` } : {}),
            ...(los.cepM !== undefined ? { feilradius: `${los.cepM} m (sensitivitet, ikke statistisk CEP)` } : {}),
            ...(los.utenforBasislinje ? { geometri: 'UTENFOR BASISLINJE — dårlig betinget' } : {}),
            ...(los.tvetydig ? { tvetydig: 'JA — se alternativ løsning' } : {}),
            ...(los.advarsler.length > 0 ? { advarsler: los.advarsler.join(' | ') } : {}),
        },
    };
}
//# sourceMappingURL=hydrofon.js.map